import type {Component} from 'vue'
import type {WizardPageId, WizardSectionId} from '~/models/wizardIds'

export type ValidationSeverity = 'error' | 'warning'

export interface ValidationRule<TDraft = any, TConfig = any> {
  id: string
  severity: ValidationSeverity
  message: string
  path?: string
  tags?: string[]
  isValid: (draft: TDraft, config?: TConfig, context?: unknown) => boolean
}

export interface LeaveGuardRule<TDraft = any, TConfig = any> {
  id: string
  message: string
  confirmLabel: string
  cancelLabel: string
  shouldConfirm: (draft: TDraft, config?: TConfig) => boolean
}

export interface SectionRegistryEntry<TDraft = any, TConfig = any> {
  id: WizardSectionId
  component?: Component
  defaultConfig?: Partial<TConfig>
  rules?: ValidationRule<TDraft, TConfig>[]
  leaveGuards?: LeaveGuardRule<TDraft, TConfig>[]
}

export interface SectionInstance<TConfig = any> {
  sectionId: WizardSectionId
  config?: Partial<TConfig>
}

export interface SectionGroup {
  title?: string
  sections: SectionInstance[]
}

export type StepSectionEntry = SectionInstance | SectionGroup

export function isSectionGroup(entry: StepSectionEntry): entry is SectionGroup {
  return 'sections' in entry
}

export function flattenStepSections(entries: StepSectionEntry[]): SectionInstance[] {
  return entries.flatMap((entry) => isSectionGroup(entry) ? entry.sections : [entry])
}

export interface StepHideRule<TDraft = any> {
  id: string
  isHidden: (draft: TDraft) => boolean
}

export interface StepDefinition {
  Title: string
  Page: WizardPageId
  sections: StepSectionEntry[]
  hiddenWhen?: StepHideRule[]
}

function matchesSection(ids: WizardSectionId[]) {
  return (instance: SectionInstance) => ids.includes(instance.sectionId)
}

/**
 * Section-based lookups over a step table. Prefer these over comparing `Page`:
 * `Page` is the wizard's URL slug, so questions like "does this flow collect an
 * environment report?" belong to the sections a step renders, not to its name.
 */
export function stepsIncludeSection(steps: StepDefinition[], ...sectionIds: WizardSectionId[]): boolean {
  return steps.some((step) => flattenStepSections(step.sections).some(matchesSection(sectionIds)))
}

export function findStepWithSection(steps: StepDefinition[], ...sectionIds: WizardSectionId[]): StepDefinition | undefined {
  return steps.find((step) => flattenStepSections(step.sections).some(matchesSection(sectionIds)))
}

export function findSectionInstance(steps: StepDefinition[], ...sectionIds: WizardSectionId[]): SectionInstance | undefined {
  return steps.flatMap((step) => flattenStepSections(step.sections)).find(matchesSection(sectionIds))
}
